import { useEffect, useState } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { getMyUniforms, requestUniform, GetMyUniformsOutputType } from 'zite-endpoints-sdk';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Shirt, Plus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

type Allocation = GetMyUniformsOutputType['allocations'][0];
type Request = GetMyUniformsOutputType['requests'][0];

const fmtDate = (d?: string) => { try { return d ? format(parseISO(d.includes('T') ? d : d + 'T00:00:00'), 'dd MMM yyyy') : '—'; } catch { return d || '—'; } };

const statusColor: Record<string, string> = {
  Pending: 'bg-muted text-muted-foreground',
  Ordered: 'bg-primary/10 text-primary',
  Fulfilled: 'bg-primary text-primary-foreground',
  Rejected: 'bg-destructive/10 text-destructive',
};

export default function Uniform() {
  const { user } = useAuth();
  const [allocations, setAllocations] = useState<Allocation[]>([]);
  const [requests, setRequests] = useState<Request[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [itemType, setItemType] = useState<string>('');
  const [size, setSize] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);

  const isManager = user?.role === 'Manager' || user?.role === 'Admin' || user?.role === 'Owner';
  const availableItems = isManager ? ['T-Shirt', 'Polo', 'Shirt'] : ['T-Shirt', 'Polo'];

  const load = () => {
    getMyUniforms({})
      .then(d => { setAllocations(d.allocations); setRequests(d.requests); })
      .catch(() => toast.error('Failed to load uniform data'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { if (user) load(); }, [user]);

  const handleRequest = async () => {
    if (!itemType || !size) { toast.error('Please select an item and size'); return; }
    setSubmitting(true);
    try {
      await requestUniform({ itemType: itemType as any, size: size as any });
      toast.success('Request submitted');
      setDialogOpen(false);
      setItemType('');
      setSize('');
      load();
    } catch { toast.error('Failed to submit request'); }
    finally { setSubmitting(false); }
  };

  if (loading) {
    return (
      <div className="px-6 py-8 max-w-2xl mx-auto space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40" />
        <Skeleton className="h-40" />
      </div>
    );
  }

  return (
    <div className="px-4 md:px-6 py-6 md:py-8 max-w-2xl mx-auto pb-20 md:pb-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-foreground flex items-center gap-2">
            <Shirt className="w-5 h-5 text-primary" /> My Uniform
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Your current allocation and request new items</p>
        </div>
        <Button onClick={() => setDialogOpen(true)} size="sm">
          <Plus className="w-4 h-4 mr-1.5" /> Request Uniform
        </Button>
      </div>

      {/* Current allocations */}
      <div className="mb-6">
        <p className="text-sm font-semibold text-foreground mb-3">Current Allocation</p>
        {allocations.length === 0 ? (
          <div className="border border-border rounded-lg p-6 text-center text-sm text-muted-foreground">
            No uniform items allocated yet
          </div>
        ) : (
          <div className="border border-border rounded-lg divide-y divide-border">
            {allocations.map(a => (
              <div key={a.id} className="px-4 py-3 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-foreground">{a.itemType}</p>
                  <p className="text-xs text-muted-foreground">Size: {a.size}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold text-foreground">{a.quantity}</p>
                  <p className="text-xs text-muted-foreground">{a.lastIssued ? `Last issued ${fmtDate(a.lastIssued)}` : 'No issue date'}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Requests */}
      <div>
        <p className="text-sm font-semibold text-foreground mb-3">My Requests</p>
        {requests.length === 0 ? (
          <div className="border border-border rounded-lg p-6 text-center text-sm text-muted-foreground">
            No requests yet
          </div>
        ) : (
          <div className="border border-border rounded-lg divide-y divide-border">
            {requests.map(r => (
              <div key={r.id} className="px-4 py-3 flex items-center justify-between">
                <div>
                  <p className="text-sm text-foreground">{r.itemType} — {r.size}</p>
                  <p className="text-xs text-muted-foreground">Requested {r.requestedAt ? fmtDate(r.requestedAt) : ''}</p>
                  {r.orderNotes && <p className="text-xs text-muted-foreground italic mt-0.5">{r.orderNotes}</p>}
                </div>
                <span className={`text-xs px-2 py-0.5 rounded ${statusColor[r.status] || statusColor.Pending}`}>
                  {r.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Request Uniform Item</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider block mb-1.5">Item Type</label>
              <Select value={itemType} onValueChange={setItemType}>
                <SelectTrigger><SelectValue placeholder="Select item" /></SelectTrigger>
                <SelectContent>
                  {availableItems.map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider block mb-1.5">Size</label>
              <Select value={size} onValueChange={setSize}>
                <SelectTrigger><SelectValue placeholder="Select size" /></SelectTrigger>
                <SelectContent>
                  {['XS', 'S', 'M', 'L', 'XL', 'XXL'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleRequest} disabled={submitting || !itemType || !size} className="w-full">
              {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</> : 'Submit Request'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
